import { DatePickerProps, Form, message, notification } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import omitBy from 'lodash/omitBy';
import isNil from 'lodash/isNil';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
  useCreateChannelMutation,
  useGetChannelOptionsQuery,
  useGetDspOptionsQuery,
  useUpdateChannelMutation,
  mediaPlanUtil
} from '@/api';
import { API_DATE_FORMAT, NAMESPACE, TAG } from '@/constants';
import { DEFAULT_CURRENCY } from '@/constants/currency';
import { ChannelForm, Context, UseChannelForm, Warnings } from '../ChannelForm.types';
import { Channel } from '@/models';
import { INITIAL_STATE, STEPS, getBookingFieldNames, getStepFieldNames } from '../ChannelForm.constants';
import { useChannelOptions } from './useChannelOptions';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { isMeasurementChannel } from '../../../utils/measurementChannel';
import { isRateCardNotFoundError } from '../../../utils/rateCardLookup';
import { useRateCardBenchmarkSync } from '../../../hooks/useRateCardBenchmarkSync';
import {
  mergeChannelFormValues,
  mergeLineItemLayers,
  normalizeFormatsField,
  normalizeProductField,
  pickLineItemBaseline
} from '../../../utils/normalizeChannelFormPayload';
import { getLineItemFormMode, isPrefilledMode } from '../../../utils/lineItemFormMode';
import { channelLineDefaults, countryForCurrency, planFlightDates } from '../../../utils/lineItemDefaults';
import { useCountryFollowsCurrency } from './useCountryFollowsCurrency';

const formatDate = (data: DatePickerProps['value']): string | undefined => data?.format(API_DATE_FORMAT);

const convertFlightDates = (
  flightDates: ChannelForm['flightDates']
): Pick<Channel, 'startDate' | 'endDate'> => ({
  startDate: formatDate(flightDates?.[0]) as unknown as Date,
  endDate: formatDate(flightDates?.[1]) as unknown as Date
});

notification.config({ maxCount: 1 });

export const useChannelForm: UseChannelForm = ({
  mediaPlanId,
  totalBudget,
  planCurrency,
  planStartDate,
  planEndDate,
  channel: channelData,
  close
}) => {
  const { t } = useTranslation(NAMESPACE.MEDIA_PLAN_PAGE);
  const dispatch = useAppDispatch();
  const [currentStep, setCurrentStep] = useState(0);
  /* Decided once from the line the form opened with — every create/edit
     branch below reads these, never `channelData?._id` directly. */
  const mode = getLineItemFormMode(channelData);
  const isPrefilled = isPrefilledMode(mode);
  /** Submit creates a new line (a duplicate included) rather than updating one. */
  const isCreating = mode !== 'edit';
  const [formData, setFormData] = useState<ChannelForm>(() =>
    isPrefilled ? pickLineItemBaseline(channelData) : { channel: channelData?.channel }
  );

  /** Line item for edit flows comes from the channel list only (no GET /channels/v1/:id on backend). */
  const channelFromApi = channelData;
  const [isFormTouched, setIsFormTouched] = useState(false);
  const [isFormValid, setIsFormValid] = useState(true);
  const [warnings, setWarnings] = useState<Warnings>(new Map());
  const [createChannel, { isLoading: isSubmitting }] = useCreateChannelMutation();
  const [updateChannel] = useUpdateChannelMutation();

  const [form] = Form.useForm<ChannelForm>();
  const { options } = useChannelOptions(form.getFieldsValue());
  const currentChannelType = Form.useWatch('channel', form);
  const currentProductType = Form.useWatch('product', form);
  const currentKpiType = Form.useWatch('kpi', form);
  const currentFormats = Form.useWatch('formats', form);
  const currentBudget = Form.useWatch('budget', form);
  const currentSellRate = Form.useWatch('sellRate', form);
  const currentBuyingMetric = Form.useWatch('buyingMetric', form);
  const currentPackageId = Form.useWatch('packageId', form);
  const currentFeePercent = Form.useWatch('feePercent', form);
  const currentFeeBaseChannel = Form.useWatch('feeBaseChannel', form);
  const currentApplyAgencyDiscount = Form.useWatch('applyAgencyDiscount', form);
  const currentValueAddInputMode = Form.useWatch('valueAddInputMode', form);
  const currentValueAddType = Form.useWatch('valueAddType', form);
  const currentValueAddPercent = Form.useWatch('valueAddPercent', form);
  const currentValueAddAbsolute = Form.useWatch('valueAddAbsolute', form);
  const currentRateSource = Form.useWatch('rateSource', form) as
    | 'MANUAL'
    | 'PACKAGE'
    | 'TECH_FEE'
    | undefined;
  const selectedChannelType = currentChannelType || formData?.channel;
  const selectedProductType = currentProductType || formData?.product;
  const selectedKpiType = currentKpiType || formData?.kpi;
  const selectedFormats = currentFormats ?? formData?.formats;
  const selectedBudget = currentBudget ?? formData?.budget;
  const selectedSellRate = currentSellRate ?? formData?.sellRate;
  const selectedBuyingMetric = currentBuyingMetric ?? formData?.buyingMetric;
  const selectedPackageId = currentPackageId ?? formData?.packageId;
  const selectedFeePercent = currentFeePercent ?? formData?.feePercent;
  const selectedFeeBaseChannel = currentFeeBaseChannel ?? formData?.feeBaseChannel;
  const selectedApplyAgencyDiscount =
    currentApplyAgencyDiscount ?? formData?.applyAgencyDiscount ?? true;
  /* The live field wins over the last step's snapshot in `formData`, as for
     every field above. OR-ing the two kept a line edited from ADDED_VALUE to
     VALUE_POT previewing as ADDED_VALUE until the step changed. */
  const selectedValueAddInputMode: Context['valueAddInputMode'] =
    (currentValueAddInputMode ?? formData?.valueAddInputMode) === 'ABSOLUTE' ? 'ABSOLUTE' : 'PERCENT';
  const selectedValueAddType: Context['valueAddType'] =
    (currentValueAddType ?? formData?.valueAddType) === 'ADDED_VALUE' ? 'ADDED_VALUE' : 'VALUE_POT';
  const selectedValueAddPercent = currentValueAddPercent ?? formData?.valueAddPercent;
  const selectedValueAddAbsolute = currentValueAddAbsolute ?? formData?.valueAddAbsolute;

  const { benchmark: resolvedBenchmark, hasBenchmarkConflict } = useRateCardBenchmarkSync({
    form,
    channel: selectedChannelType,
    product: selectedProductType,
    kpi: selectedKpiType,
    formats: selectedFormats,
    rateSource: currentRateSource ?? formData?.rateSource ?? 'MANUAL'
  });

  const { data: channelOptionsData } = useGetChannelOptionsQuery();
  const { data: dspOptionsData } = useGetDspOptionsQuery();
  const activeDspNames = useMemo(() => dspOptionsData?.dsps?.map((d) => d.dspName), [dspOptionsData]);

  /* Picking a channel resets the channel-specific fields, then applies that
     channel's defaults from Trader Controls: its targeting details and DSP.
     Flight dates, country and currency do not depend on the channel, so they
     are kept — dropping the dates threw away the campaign-date default. */
  useEffect(() => {
    if (isFormTouched && currentChannelType && currentChannelType !== formData?.channel) {
      const isMeasurement = isMeasurementChannel(currentChannelType);
      const defaults = channelLineDefaults(currentChannelType, channelOptionsData?.channels, activeDspNames);
      const previousDefaults = channelLineDefaults(formData?.channel, channelOptionsData?.channels, activeDspNames);
      const currentDsp = form.getFieldValue('dsp');
      /* The new channel's DSP wins; otherwise keep the DSP unless it was only
         there as the previous channel's default. */
      const dsp = defaults.dsp ?? (currentDsp === previousDefaults.dsp ? undefined : currentDsp);
      const preserved = {
        flightDates: form.getFieldValue('flightDates'),
        country: form.getFieldValue('country'),
        currency: form.getFieldValue('currency'),
        ...(isMeasurement ? {} : { dsp })
      };
      const data = {
        ...INITIAL_STATE,
        channel: currentChannelType,
        ...preserved,
        ...(defaults.targetingDetails && { targetingDetails: defaults.targetingDetails })
      };
      setFormData(data);
      form.setFieldsValue(data);
    }
  }, [
    currentChannelType,
    form,
    isFormTouched,
    formData?.channel,
    currentProductType,
    currentKpiType,
    channelOptionsData,
    activeDspNames
  ]);

  useCountryFollowsCurrency(form);

  const areFormValuesValid: boolean = useMemo(() => {
    // Only start enforcing rate-card option validity once we've moved past the
    // initial Line item step. On step 0, a newly added channel (e.g. CTV)
    // may not yet exist in the rate card options, and we don't want to wipe
    // the user's input before they can switch to Package.
    if (currentStep === 0) return true;

    // Package / tech fee mode should not be invalidated by missing rate card options.
    if (currentRateSource === 'PACKAGE' || currentRateSource === 'TECH_FEE') return true;

    if (!options) return true;

    const {
      channels: channelOptions,
      products: productOptions,
      formats: formatOptions
    } = options;

    const { channel, product, formats } = form.getFieldsValue();

    // Only fail when we have options to validate against; undefined/empty option
    // arrays from the API should not trigger a reset (avoids false positives).
    // KPI is excluded: it comes from Admin KPIs (MongoDB), not rate card options.
    return !(
      (channel && Array.isArray(channelOptions) && channelOptions.length > 0 && !channelOptions.includes(channel)) ||
      (product && Array.isArray(productOptions) && productOptions.length > 0 && !productOptions.includes(product)) ||
      (formats &&
        Array.isArray(formats) &&
        formats.length > 0 &&
        Array.isArray(formatOptions) &&
        formatOptions.length > 0 &&
        formats.some((option) => !formatOptions.includes(option)))
    );
  }, [form, options, currentStep, currentRateSource]);

  const handleNext = useCallback((): void => {
    const rateSource = form.getFieldValue('rateSource') as 'MANUAL' | 'PACKAGE' | 'TECH_FEE' | undefined;
    const channelName = form.getFieldValue('channel') as string | undefined;
    const fieldNames = getStepFieldNames(currentStep, rateSource, channelName);
    const validation = fieldNames.length > 0 ? form.validateFields(fieldNames) : Promise.resolve();

    validation
      .then(() => {
        setIsFormValid(true);
        setFormData((prev) => ({ ...prev, ...form.getFieldsValue(true) }));
        setCurrentStep((prevState) => prevState + 1);
      })
      .catch(() => setIsFormValid(false));
  }, [form, currentStep]);

  const handlePrev = (): void => setCurrentStep((prevState) => prevState - 1);

  const handleValuesChange = (): void => {
    setIsFormTouched(true);
    setFormData((prev) => ({ ...prev, ...form.getFieldsValue(true) }));
  };

  const handleSetWarnings = (map: Warnings) => {
    setWarnings(new Map(map));
  };

  const context = useMemo(
    () => ({
      channel: selectedChannelType,
      mediaPlanId,
      totalBudget,
      planCurrency,
      ...formData,
      rateSource: currentRateSource ?? formData?.rateSource ?? 'MANUAL',
      setWarnings: handleSetWarnings,
      warnings,
      /* Only the line being saved over: this id is excluded from the plan's
         budget totals, and a duplicate adds to them rather than replacing. */
      _id: mode === 'edit' ? channelData?._id : undefined,
      isPrefilled,
      product: selectedProductType,
      kpi: selectedKpiType,
      formats: selectedFormats,
      budget: selectedBudget,
      sellRate: selectedSellRate,
      buyingMetric: selectedBuyingMetric,
      packageId: selectedPackageId,
      feePercent: selectedFeePercent,
      feeBaseChannel: selectedFeeBaseChannel,
      applyAgencyDiscount: selectedApplyAgencyDiscount,
      valueAddType: selectedValueAddType,
      valueAddInputMode: selectedValueAddInputMode,
      valueAddPercent: selectedValueAddPercent,
      valueAddAbsolute: selectedValueAddAbsolute,
      benchmark: resolvedBenchmark,
      hasBenchmarkConflict
    }),
    [
      selectedChannelType,
      mediaPlanId,
      totalBudget,
      planCurrency,
      formData,
      currentRateSource,
      warnings,
      mode,
      channelData?._id,
      isPrefilled,
      selectedProductType,
      selectedKpiType,
      selectedFormats,
      selectedBudget,
      selectedSellRate,
      selectedBuyingMetric,
      selectedPackageId,
      selectedFeePercent,
      selectedFeeBaseChannel,
      selectedApplyAgencyDiscount,
      selectedValueAddType,
      selectedValueAddInputMode,
      selectedValueAddPercent,
      selectedValueAddAbsolute,
      resolvedBenchmark,
      hasBenchmarkConflict
    ]
  );

  const initialValues: ChannelForm = useMemo(() => {
    if (!channelFromApi) {
      const currency = planCurrency ?? DEFAULT_CURRENCY;
      const flightDates = planFlightDates(planStartDate, planEndDate);
      const country = countryForCurrency(currency);
      return {
        /* A new line runs for the campaign and is booked where its currency is
           spent, until the trader says otherwise. */
        ...(flightDates && { flightDates }),
        ...(country && { country }),
        poweredBySkyrise: false,
        rateSource: 'MANUAL' as any,
        currency,
        applyAgencyDiscount: true,
        valueAddType: 'VALUE_POT' as const,
        valueAddInputMode: 'PERCENT' as const
      };
    }

    const { startDate, endDate, format: _legacyFormat, ...rest } = channelFromApi as any;
    const formats = normalizeFormatsField(channelFromApi as ChannelForm);

    return {
      ...(startDate && endDate && { flightDates: [dayjs(startDate), dayjs(endDate)] }),
      ...rest,
      country: rest.country,
      currency: rest.currency?.trim() || planCurrency || DEFAULT_CURRENCY,
      dsp: rest.dsp,
      kpi: rest.kpi,
      product: normalizeProductField(rest.product),
      ...(formats ? { formats } : {}),
      rateSource: rest.rateSource ?? 'MANUAL',
      applyAgencyDiscount: rest.applyAgencyDiscount !== false,
      valueAddType: rest.valueAddType === 'ADDED_VALUE' ? 'ADDED_VALUE' : 'VALUE_POT',
      valueAddInputMode: rest.valueAddInputMode === 'ABSOLUTE' ? 'ABSOLUTE' : 'PERCENT'
    };
  }, [channelFromApi, planCurrency, planStartDate, planEndDate]);

  useEffect(() => {
    if (isPrefilled && initialValues) {
      form.setFieldsValue(initialValues);
      setFormData((prev) => ({ ...prev, ...initialValues }));
    }
  }, [isPrefilled, form, initialValues]);

  useEffect(() => {
    const isOnFinalStep = currentStep >= STEPS.length - 1;
    if (
      !areFormValuesValid &&
      currentStep > 0 &&
      currentRateSource === 'MANUAL' &&
      !isOnFinalStep &&
      !isSubmitting &&
      !isPrefilled
    ) {
      const preserved = {
        country: form.getFieldValue('country'),
        currency: form.getFieldValue('currency'),
        dsp: form.getFieldValue('dsp')
      };
      form.setFieldsValue({ ...INITIAL_STATE, ...preserved });
      notification.warning({
        message: t('channelDependenciesWarning'),
        duration: 0
      });
    }
  }, [areFormValuesValid, currentStep, currentRateSource, form, t, isSubmitting, isPrefilled]);

  const getFormValues = useCallback(
    async (isDraft: boolean): Promise<ChannelForm> => {
      const allValues = mergeChannelFormValues(form.getFieldsValue(true), formData, channelData);
      if (selectedChannelType && isDraft) {
        await form.validateFields(
          getBookingFieldNames(allValues.rateSource, allValues.channel ?? selectedChannelType)
        );
        return mergeChannelFormValues(form.getFieldsValue(true), formData, channelData);
      }
      await form.validateFields();
      return mergeChannelFormValues(form.getFieldsValue(true), formData, channelData);
    },
    [form, formData, channelData, selectedChannelType]
  );

  const submit = async (saveAsDraft: boolean) => {
    let merged: ChannelForm;
    try {
      merged = await getFormValues(saveAsDraft);
    } catch {
      return;
    }
    const withBaseline = isPrefilled
      ? mergeLineItemLayers(pickLineItemBaseline(channelData), merged)
      : merged;
    const { flightDates, mediaPlan, ...rest } = withBaseline;

    const dates = convertFlightDates(flightDates);
    const normalizedRest: typeof rest = { ...rest };
    delete (normalizedRest as any).kpis;
    delete (normalizedRest as any).benchmark;
    if (normalizedRest.rateSource === 'PACKAGE') {
      normalizedRest.buyingMetric = 'CPM' as any;
      // Keep sellRate (package sellCpm) so metrics API and list margin columns can compute.
      delete (normalizedRest as any).feePercent;
      delete (normalizedRest as any).feeBaseChannel;
    } else if (normalizedRest.rateSource === 'TECH_FEE') {
      normalizedRest.buyingMetric = 'FLAT' as any;
      delete (normalizedRest as any).packageId;
      delete (normalizedRest as any).dsp;
      // Fee is always % of the line’s own channel spend on the plan.
      if (!normalizedRest.feeBaseChannel && normalizedRest.channel) {
        normalizedRest.feeBaseChannel = normalizedRest.channel;
      }
      if (!normalizedRest.kpi?.trim()) {
        normalizedRest.kpi = 'N/A' as any;
      }
      normalizedRest.formats = normalizedRest.formats?.length ? normalizedRest.formats : (['Tech Fee'] as any);
      if (!normalizedRest.product) {
        normalizedRest.product = `Tech fee (${normalizedRest.feePercent}% of ${normalizedRest.feeBaseChannel})`;
      }
      if (normalizedRest.applyAgencyDiscount === undefined) {
        normalizedRest.applyAgencyDiscount = true;
      }
    } else {
      normalizedRest.rateSource = 'MANUAL';
      delete (normalizedRest as any).packageId;
      delete (normalizedRest as any).feePercent;
      delete (normalizedRest as any).feeBaseChannel;
    }
    if (normalizedRest.applyAgencyDiscount === undefined) {
      normalizedRest.applyAgencyDiscount = true;
    }
    if (normalizedRest.valueAddType !== 'ADDED_VALUE') {
      normalizedRest.valueAddType = 'VALUE_POT';
    }
    if (normalizedRest.valueAddInputMode !== 'ABSOLUTE') {
      normalizedRest.valueAddInputMode = 'PERCENT';
    }
    if (normalizedRest.valueAddPercent == null || !Number.isFinite(Number(normalizedRest.valueAddPercent))) {
      normalizedRest.valueAddPercent = 0;
    }
    if (normalizedRest.valueAddAbsolute == null || !Number.isFinite(Number(normalizedRest.valueAddAbsolute))) {
      normalizedRest.valueAddAbsolute = 0;
    }

    const productStr = normalizeProductField(normalizedRest.product);
    if (productStr != null) normalizedRest.product = productStr;
    const normalizedFormats = normalizeFormatsField(normalizedRest);
    if (normalizedFormats) {
      normalizedRest.formats = normalizedFormats;
    }
    delete (normalizedRest as { format?: unknown }).format;
    if (
      isMeasurementChannel(normalizedRest.channel) ||
      normalizedRest.rateSource === 'PACKAGE' ||
      normalizedRest.rateSource === 'TECH_FEE'
    ) {
      delete normalizedRest.dsp;
    }

    if (
      !saveAsDraft &&
      normalizedRest.rateSource === 'TECH_FEE' &&
      (!normalizedRest.feeBaseChannel || !(Number(normalizedRest.feePercent) > 0))
    ) {
      message.error(t('techFeeFieldsRequired'));
      return;
    }

    if (
      !saveAsDraft &&
      normalizedRest.rateSource !== 'PACKAGE' &&
      normalizedRest.rateSource !== 'TECH_FEE' &&
      (!normalizedRest.channel || !normalizedRest.product || !normalizedRest.kpi || !normalizedRest.formats?.length)
    ) {
      message.error(t('rateCardNotFoundForLineItem'));
      if (import.meta.env.DEV) {
        console.error('[ChannelForm] Missing rate card fields before submit:', {
          channel: normalizedRest.channel,
          product: normalizedRest.product,
          kpi: normalizedRest.kpi,
          formats: normalizedRest.formats,
          channelData
        });
      }
      return;
    }

    const payload: Record<string, any> = isCreating
      ? { mediaPlan: mediaPlanId, saveAsDraft, ...omitBy(normalizedRest, isNil), ...dates }
      : { _id: channelData?._id, saveAsDraft, ...omitBy(normalizedRest, isNil), ...dates };
    if (import.meta.env.DEV) {
      console.log('[ChannelForm] Payload — verify rate card fields in Network tab:', {
        channel: payload.channel,
        product: payload.product,
        kpi: payload.kpi,
        formats: payload.formats,
        rateSource: payload.rateSource
      });
    }

    const content = isCreating ? t('channelHasBeenCreated') : t('channelHasBeenEdited');
    const mutation = isCreating
      ? createChannel(payload as Parameters<typeof createChannel>[0])
      : updateChannel(payload as Parameters<typeof updateChannel>[0]);
    return mutation
      .unwrap()
      .then(() => {
        close();
        dispatch(mediaPlanUtil.invalidateTags([TAG.MEDIA_PLAN]));
        message.success({ content, duration: 3 });
      })
      .catch((err: any) => {
        const data = err?.data ?? err?.error ?? err?.response?.data;
        const backendMessage =
          (typeof data?.message === 'string' ? data.message : null) ??
          (Array.isArray(data?.message) ? data.message.join('; ') : null) ??
          (typeof data?.error === 'string' ? data.error : null) ??
          err?.error ??
          err?.message ??
          (typeof err === 'string' ? err : '');
        const errorsObj = data?.errors ?? data?.error ?? data;
        const failedFields = data?.failedFields ?? (Array.isArray(errorsObj) ? errorsObj : undefined);
        const errorsDetail =
          errorsObj && typeof errorsObj === 'object' && !Array.isArray(errorsObj)
            ? Object.entries(errorsObj)
                .filter(([k]) => !['statusCode', 'error', 'message'].includes(k))
                .map(([k, v]: [string, any]) =>
                  v && typeof v === 'object' && v?.message ? `${k}: ${v.message}` : `${k}: ${String(v)}`
                )
                .join('; ')
            : '';
        const fullMessage = [backendMessage, errorsDetail].filter(Boolean).join(errorsDetail ? ' — ' : '');
        const payloadSnapshot = {
          ...payload,
          formats: payload?.formats,
          kpi: payload?.kpi,
          product: payload?.product,
          packageId: payload?.packageId,
          rateSource: payload?.rateSource
        };
        console.error('[ChannelForm] Submit error — full response:', {
          data,
          payload: payloadSnapshot,
          fullMessage,
          failedFields
        });
        const isLineItemValidation =
          typeof fullMessage === 'string' &&
          (fullMessage.toLowerCase().includes('lineitem') ||
            fullMessage.toLowerCase().includes('line item') ||
            fullMessage.toLowerCase().includes('validation failed'));
        const hasKpiError =
          (Array.isArray(failedFields) && failedFields.includes('kpi')) ||
          (typeof fullMessage === 'string' &&
            (fullMessage.toLowerCase().includes("path 'kpi'") ||
              fullMessage.toLowerCase().includes('kpi is required') ||
              fullMessage.toLowerCase().includes("'kpi'")));
        const hasFailedFields = Array.isArray(failedFields) && failedFields.length > 0;
        const hasProductError = hasFailedFields && failedFields.includes('product');
        const isLineItemNotFound =
          typeof fullMessage === 'string' &&
          fullMessage.toLowerCase().includes('not found') &&
          fullMessage.toLowerCase().includes('channel');
        const isMissingRateCard = isRateCardNotFoundError(fullMessage);
        const displayMessage = isLineItemNotFound
          ? t('lineItemNotFound')
          : isMissingRateCard
            ? `${t('rateCardNotFoundForLineItem')}${backendMessage ? ` (${backendMessage})` : ''}`
            : hasKpiError
            ? t('kpiRequiredForLineItem')
            : hasProductError
              ? t('productRequiredForLineItem')
              : hasFailedFields
                ? t('lineItemFieldRequired', { field: failedFields.join(', ') })
                : fullMessage || t('lineItemValidationFailed');
        if (normalizedRest.rateSource === 'PACKAGE') {
          message.error(
            backendMessage?.toLowerCase().includes('sell') || backendMessage?.toLowerCase().includes('cpm')
              ? t('packageMissingCpm')
              : displayMessage
          );
          return;
        }
        message.error(displayMessage);
      });
  };

  return {
    currentStep,
    form,
    handleNext,
    handlePrev,
    handleValuesChange,
    isFormValid,
    initialValues,
    submit,
    isFormTouched,
    isSubmitting,
    context
  };
};
